import { useCallback } from 'react';
import styled from 'styled-components';

import Card from 'components/insights/layout/Card';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import { ScrollContainer } from 'components/shared/DataTable';
import { useReferralsInsightsActions, useReferralsInsightsStore } from 'stores/referralsInsightsStore';

import { getEntityColumnHeader, VIEW_BY_DIMENSION_MAP } from '../constants';
import { useReferralsInsightsContext } from '../ReferralsContainer';

import { EntityButton } from './referralDetailsColumns';
import ReferralDetailsHeader from './ReferralDetailsHeader';
import ReferralDetailsTable from './ReferralDetailsTable';

type Props = {
  loading: boolean;
  rollups: AnalyticsResponseRollupCollection;
};

export default function ReferralDetailsSection({ loading, rollups }: Props) {
  const { predecessorGroupType } = useReferralsInsightsContext();
  const viewBy = useReferralsInsightsStore((state) => state.viewBy);
  const selectedReceivingGroupType = useReferralsInsightsStore((state) => state.selectedReceivingGroupType);
  const { openSlideout } = useReferralsInsightsActions();

  const dimension = VIEW_BY_DIMENSION_MAP[viewBy];
  const predecessorGroupTypeName = predecessorGroupType?.displayName ?? '';
  const receivingGroupTypeName = selectedReceivingGroupType?.displayName ?? '';
  const entityHeader = getEntityColumnHeader(dimension, {
    sendingGroupTypeName: predecessorGroupTypeName,
    receivingGroupTypeName,
  });

  const entityCell = useCallback(
    (info: { getValue: () => string; row: { original: { entityId: string; entityName: string } } }) => {
      const row = info.row.original;
      if (!row.entityId) return <span>{info.getValue()}</span>;
      return (
        <EntityButton
          type='button'
          onClick={() =>
            openSlideout({
              entityId: row.entityId,
              entityName: row.entityName,
              dimension,
            })
          }>
          {info.getValue()}
        </EntityButton>
      );
    },
    [dimension, openSlideout]
  );

  return (
    <SectionCard>
      <ReferralDetailsHeader />
      <TableWrapper>
        <ReferralDetailsTable
          loading={loading}
          rollups={rollups}
          dimension={dimension}
          entityHeader={entityHeader}
          entityCell={entityCell}
        />
      </TableWrapper>
    </SectionCard>
  );
}

const SectionCard = styled(Card)`
  padding: 0;
  display: flex;
  flex-direction: column;
  border-radius: 4px;
`;

const TableWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 0 24px 24px;

  td:first-child:not(:last-child),
  th:first-child {
    border-right: 1px solid ${({ theme }) => theme.colors.black10};
  }

  @media (max-width: 800px) {
    padding: 0;

    ${ScrollContainer} {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
      border-left: none;
      border-right: none;
      border-bottom: none;
    }
  }
`;
